import {
  createUnvalidatedValidationSummary,
  type CoasterProject,
  type TrackBehavior,
  type Vec3,
} from '../schema';
import { generateVerticalLoopProfile } from '../editor/semantic-elements.ts';
import { appendAuthoredPiece } from '../builder-v2/index.ts';
import type { AuthoredGridPose } from '../schema/index.ts';

const FIXTURE_TIME = '2026-07-15T00:00:00.000Z';

type FixtureNode = {
  id: string;
  position: Vec3;
  bankDegrees?: number;
  rollWindingDegrees?: number;
};

function makeProject(
  id: string,
  title: string,
  nodes: FixtureNode[],
  behaviors: TrackBehavior[],
  closed = true,
  pieceDefinitions: Array<CoasterProject['editor']['pieceGroups'][number]['definitionId']> = [],
  plotSizeMeters: { x: number; z: number } = { x: 160, z: 120 },
  blankGeometry: 'authored' | 'modular' | 'legacy' = id === 'fixture.blank' ? 'authored' : 'legacy',
): CoasterProject {
  const trackNodes = nodes.map((node) => ({
    id: node.id,
    position: node.position,
    bankDegrees: node.bankDegrees ?? 0,
    rollWindingDegrees: node.rollWindingDegrees ?? 0,
  }));
  const sectionCount = closed ? trackNodes.length : Math.max(0, trackNodes.length - 1);
  const sections = Array.from({ length: sectionCount }, (_, index) => ({
    id: `section:${id}:${index}`,
    fromNodeId: trackNodes[index]!.id,
    toNodeId: trackNodes[(index + 1) % trackNodes.length]!.id,
    behavior: behaviors[index] ?? ({ kind: 'normal' } as const),
  }));
  const pieceGroups: CoasterProject['editor']['pieceGroups'] = [];
  sections.forEach((section, index) => {
    const definitionId =
      pieceDefinitions[index] ??
      (section.behavior.kind === 'normal'
        ? 'straight'
        : section.behavior.kind === 'brake'
          ? 'brake'
          : section.behavior.kind);
    const canJoin = [
      'station',
      'lift',
      'launch',
      'brake',
      'photo',
      'loop',
      'loop_left',
      'loop_right',
      'roll_left',
      'roll_right',
      'corkscrew_left',
      'corkscrew_right',
    ].includes(definitionId);
    const previous = pieceGroups.at(-1);
    if (canJoin && previous?.definitionId === definitionId) previous.sectionIds.push(section.id);
    else
      pieceGroups.push({
        id: `piece:${id}:${pieceGroups.length}`,
        definitionId,
        sectionIds: [section.id],
        parameters: { length: 'medium' },
      });
  });
  return {
    schemaVersion: 8,
    projectId: id,
    revisionId: `${id}:fixture-v1`,
    coasterFamilyId: 'steel.standard',
    metadata: {
      title,
      description: 'Deterministic Buncha Coasters fixture',
      createdAt: FIXTURE_TIME,
      updatedAt: FIXTURE_TIME,
      creator: { kind: 'local', id: null, displayName: 'Fixture creator' },
      tags: ['fixture'],
    },
    plot: {
      shape: 'rectangle',
      sizeMeters: plotSizeMeters,
      origin: { x: 0, y: 0, z: 0 },
      groundElevationMeters: 0,
    },
    track:
      blankGeometry === 'authored'
        ? {
            geometryKind: 'authored_grid_v2',
            splineKind: 'piecewise_bezier',
            degree: 3,
            closed,
            grid: {
              fixedUnitsPerTile: 32,
              metersPerTile: 4,
              origin: { x: 0, y: 0, z: 0 },
            },
            nodes: trackNodes,
            sections: [],
            pieces: [],
            authoredTopology: { version: 1, components: [] },
          }
        : blankGeometry === 'modular'
          ? {
              geometryKind: 'modular_piecewise_bezier',
              splineKind: 'piecewise_bezier',
              degree: 3,
              closed,
              nodes: trackNodes,
              sections: [],
            }
          : {
              geometryKind: 'legacy_cubic_bspline',
              splineKind: 'cubic_bspline',
              degree: 3,
              closed,
              nodes: trackNodes,
              sections,
            },
    editor: {
      pieceGroups,
    },
    train: {
      familyId: 'steel.standard',
      styleId: 'train.standard',
      carCount: 5,
      carSpacingMeters: 2.4,
      emptyMassKg: 4_000,
      capacityPerCar: 4,
    },
    appearance: {
      environmentThemeId: 'park_model',
      trackColor: '#2457b2',
      supportColor: '#343a40',
      trainColor: '#d94a38',
      stationColor: '#d8d2c5',
      trackMaterialId: 'painted_steel',
      supportMaterialId: 'painted_steel',
      trainMaterialId: 'painted_steel',
    },
    supports: { mode: 'automatic', suppressedSectionIds: [], manual: [] },
    scenery: [],
    highlights: sections.length
      ? [
          {
            id: `highlight:${id}:best`,
            kind: 'creator',
            sectionId: sections[Math.min(1, sections.length - 1)]!.id,
            distanceAlongSection: 0.5,
            label: 'Best moment',
          },
        ]
      : [],
    permissions: { visibility: 'private', allowReadOnlyShare: false, allowRemix: false },
    validation: createUnvalidatedValidationSummary(),
    heroCamera: {
      position: { x: 110, y: 90, z: 110 },
      target: { x: 0, y: 15, z: 0 },
      verticalFovDegrees: 45,
      nearMeters: 0.1,
      farMeters: 1_000,
      safeMargin: 0.08,
    },
    lineage: null,
  };
}

export const blankCoasterFixture = makeProject('fixture.blank', 'Blank Plot', [], [], false);

const FINISH_LESSON_ORIGIN: AuthoredGridPose = {
  x: -64,
  y: 32,
  z: 0,
  headingStep: 0,
  pitchDegrees: 0,
  bank: 'level',
  orientation: 'upright',
  windingQuarterTurns: 0,
};

function createFinishLessonFixture(): CoasterProject {
  let project = structuredClone(blankCoasterFixture);
  const sequence = [
    ['station.3t', 'station'],
    ['straight.1t', 'normal'],
    ['slope.0.25', 'lift'],
    ['slope.25.hold.run1t', 'lift'],
    ['slope.25.hold.run1t', 'lift'],
    ['slope.25.hold.run1t', 'lift'],
    ['slope.25.hold.run1t', 'lift'],
    ['slope.25.hold.run1t', 'lift'],
    ['slope.25.hold.run1t', 'lift'],
    ['slope.25.hold.run1t', 'lift'],
    ['slope.25.hold.run1t', 'lift'],
    ['slope.25.hold.run1t', 'lift'],
    ['slope.25.0', 'lift'],
    ['turn.left.90.medium', 'normal'],
    ['turn.left.90.medium', 'normal'],
    ['slope.0.n25', 'normal'],
    ['slope.n25.hold.run1t', 'normal'],
    ['slope.n25.hold.run1t', 'normal'],
    ['slope.n25.hold.run1t', 'normal'],
    ['slope.n25.hold.run1t', 'normal'],
    ['slope.n25.hold.run1t', 'normal'],
    ['slope.n25.hold.run1t', 'normal'],
    ['slope.n25.hold.run1t', 'normal'],
    ['slope.n25.hold.run1t', 'normal'],
    ['slope.n25.hold.run1t', 'normal'],
    ['slope.n25.0', 'normal'],
    ['loop.right', 'normal'],
    ['turn.right.90.medium', 'normal'],
    ['bank.level.right.1t', 'normal'],
    ['helix.right.up', 'normal'],
    ['bank.right.level.1t', 'normal'],
  ] as const;
  sequence.forEach(([descriptorId, trackType], index) => {
    const appended = appendAuthoredPiece(
      project,
      descriptorId,
      index === 0 ? FINISH_LESSON_ORIGIN : undefined,
      trackType,
    );
    if (!appended.ok) throw new Error(`Finish lesson fixture failed at ${descriptorId}.`);
    project = appended.value;
  });
  project.projectId = 'fixture.finish-this-coaster';
  project.revisionId = 'fixture.finish-this-coaster:v1';
  project.metadata.title = 'Finish This Coaster';
  project.metadata.description =
    'A freeform challenge with a lift, drop, loop, and banked helix. Find a route home.';
  project.metadata.tags = ['fixture', 'onboarding'];
  project.metadata.creator.displayName = 'Buncha Coasters';
  project.permissions = { visibility: 'private', allowReadOnlyShare: false, allowRemix: false };
  return project;
}

export const finishLessonCoasterFixture = createFinishLessonFixture();
export const modularBlankCoasterFixture = makeProject(
  'fixture.blank.modular',
  'Modular Blank Plot',
  [],
  [],
  false,
  [],
  { x: 140, z: 100 },
  'modular',
);
export const legacyBlankCoasterFixture = makeProject(
  'fixture.blank.legacy',
  'Legacy Blank Plot',
  [],
  [],
  false,
);

export const ovalCoasterFixture = makeProject(
  'fixture.oval',
  'Simple Oval',
  [
    { id: 'node:oval:0', position: { x: -34, y: 4, z: -18 } },
    { id: 'node:oval:1', position: { x: -27, y: 4, z: -18 } },
    { id: 'node:oval:2', position: { x: -20, y: 4, z: -18 } },
    { id: 'node:oval:3', position: { x: 20, y: 4, z: -18 } },
    { id: 'node:oval:4', position: { x: 36, y: 4, z: -14 }, bankDegrees: 30 },
    { id: 'node:oval:5', position: { x: 44, y: 4, z: 0 }, bankDegrees: 50 },
    { id: 'node:oval:6', position: { x: 36, y: 4, z: 14 }, bankDegrees: 30 },
    { id: 'node:oval:7', position: { x: 20, y: 4, z: 18 } },
    { id: 'node:oval:8', position: { x: -20, y: 4, z: 18 } },
    { id: 'node:oval:9', position: { x: -36, y: 4, z: 14 }, bankDegrees: 30 },
    { id: 'node:oval:10', position: { x: -44, y: 4, z: 0 }, bankDegrees: 50 },
    { id: 'node:oval:11', position: { x: -42, y: 4, z: -12 }, bankDegrees: 60 },
    { id: 'node:oval:12', position: { x: -41, y: 4, z: -18 }, bankDegrees: 45 },
  ],
  [
    { kind: 'station', holdSeconds: 2, dispatchSpeedMps: 3 },
    { kind: 'station', holdSeconds: 0, dispatchSpeedMps: 3 },
    { kind: 'launch', targetSpeedMps: 13.4112, accelerationMps2: 6 },
    { kind: 'normal' },
    { kind: 'normal' },
    { kind: 'normal' },
    { kind: 'normal' },
    { kind: 'normal' },
    { kind: 'normal' },
    { kind: 'normal' },
    { kind: 'normal' },
    { kind: 'brake', targetSpeedMps: 5, decelerationMps2: 5 },
    { kind: 'brake', targetSpeedMps: 3, decelerationMps2: 7 },
  ],
  true,
  [
    'station',
    'station',
    'launch',
    'bank_in',
    'turn_left',
    'turn_left',
    'bank_out',
    'straight',
    'straight',
    'bank_in',
    'turn_left',
    'brake',
    'brake',
  ],
);

export const liftDropCoasterFixture = makeProject(
  'fixture.lift-drop',
  'Lift & Drop',
  [
    { id: 'node:lift:0', position: { x: -48, y: 4, z: -18 } },
    { id: 'node:lift:1', position: { x: -41, y: 4, z: -18 } },
    { id: 'node:lift:2', position: { x: -34, y: 4, z: -18 } },
    { id: 'node:lift:3', position: { x: -20, y: 8, z: -22 } },
    { id: 'node:lift:4', position: { x: 2, y: 32, z: -22 } },
    { id: 'node:lift:5', position: { x: 14, y: 42, z: -17 } },
    { id: 'node:lift:6', position: { x: 28, y: 24, z: -10 } },
    { id: 'node:lift:7', position: { x: 40, y: 6, z: 0 }, bankDegrees: 15 },
    { id: 'node:lift:8', position: { x: 43, y: 5, z: 17 }, bankDegrees: 30 },
    { id: 'node:lift:9', position: { x: 28, y: 5, z: 28 }, bankDegrees: 20 },
    { id: 'node:lift:10', position: { x: 5, y: 12, z: 30 }, bankDegrees: 8 },
    { id: 'node:lift:11', position: { x: -18, y: 5, z: 27 } },
    { id: 'node:lift:12', position: { x: -40, y: 4, z: 18 } },
    { id: 'node:lift:13', position: { x: -51, y: 4, z: 5 } },
  ],
  [
    { kind: 'station', holdSeconds: 1, dispatchSpeedMps: 3.5 },
    { kind: 'station', holdSeconds: 0, dispatchSpeedMps: 3.5 },
    { kind: 'lift', targetSpeedMps: 4.5 },
    { kind: 'lift', targetSpeedMps: 4.5 },
    { kind: 'lift', targetSpeedMps: 4.5 },
    { kind: 'normal' },
    { kind: 'normal' },
    { kind: 'normal' },
    { kind: 'normal' },
    { kind: 'normal' },
    { kind: 'normal' },
    { kind: 'brake', targetSpeedMps: 3.5, decelerationMps2: 8 },
    { kind: 'brake', targetSpeedMps: 3.5, decelerationMps2: 8 },
    { kind: 'brake', targetSpeedMps: 3.5, decelerationMps2: 8 },
  ],
  true,
  [
    'station',
    'station',
    'lift',
    'lift',
    'lift',
    'drop',
    'drop',
    'bank_in',
    'turn_left',
    'bank_out',
    'straight',
    'brake',
    'brake',
    'brake',
  ],
);

const systemsCircuitNodes: FixtureNode[] = [
  { id: 'node:systems:0', position: { x: -50, y: 4, z: -8 } },
  { id: 'node:systems:1', position: { x: -45, y: 4, z: -20 } },
  { id: 'node:systems:2', position: { x: -28, y: 4, z: -24 } },
  { id: 'node:systems:3', position: { x: -8, y: 10, z: -24 } },
  { id: 'node:systems:4', position: { x: 12, y: 42, z: -17 } },
  { id: 'node:systems:5', position: { x: 39, y: 4, z: -8 }, bankDegrees: 10 },
  { id: 'node:systems:6', position: { x: 45, y: 4, z: 7 }, bankDegrees: 16 },
  { id: 'node:systems:7', position: { x: 44, y: 4, z: 22 }, bankDegrees: 20 },
  { id: 'node:systems:8', position: { x: 24, y: 4, z: 34 }, bankDegrees: 22 },
  { id: 'node:systems:9', position: { x: 2, y: 5, z: 37 }, bankDegrees: 20 },
  { id: 'node:systems:10', position: { x: -22, y: 11, z: 31 }, bankDegrees: 16 },
  { id: 'node:systems:11', position: { x: -40, y: 8, z: 21 }, bankDegrees: 12 },
  { id: 'node:systems:12', position: { x: -50, y: 4, z: 10 }, bankDegrees: 6 },
  { id: 'node:systems:13', position: { x: -53, y: 4, z: 1 } },
];

const systemsCircuitBehaviors: TrackBehavior[] = [
  { kind: 'station', holdSeconds: 1, dispatchSpeedMps: 4 },
  { kind: 'station', holdSeconds: 0, dispatchSpeedMps: 4 },
  { kind: 'lift', targetSpeedMps: 8 },
  { kind: 'lift', targetSpeedMps: 8 },
  { kind: 'normal' },
  { kind: 'normal' },
  { kind: 'launch', targetSpeedMps: 20, accelerationMps2: 7 },
  { kind: 'launch', targetSpeedMps: 20, accelerationMps2: 7 },
  { kind: 'normal' },
  { kind: 'normal' },
  { kind: 'normal' },
  { kind: 'brake', targetSpeedMps: 4, decelerationMps2: 12 },
  { kind: 'brake', targetSpeedMps: 4, decelerationMps2: 12 },
  { kind: 'station', holdSeconds: 0, dispatchSpeedMps: 4 },
];

export const extremeWarningCoasterFixture = makeProject(
  'fixture.extreme-warning',
  'Extreme Warning Test',
  systemsCircuitNodes,
  systemsCircuitBehaviors,
);

const firstLoopProfile = generateVerticalLoopProfile(28, true, 1);
const firstLoopExitX = 28 - (firstLoopProfile.at(-1)?.sideM ?? 0);
const firstCoasterNodes: FixtureNode[] = [
  { id: 'node:first:station:0', position: { x: -56, y: 4, z: -22 } },
  { id: 'node:first:station:1', position: { x: -49, y: 4, z: -22 } },
  { id: 'node:first:station:2', position: { x: -42, y: 4, z: -22 } },
  { id: 'node:first:lift:0', position: { x: -30, y: 4, z: -24 } },
  { id: 'node:first:lift:1', position: { x: -16, y: 16, z: -25 } },
  { id: 'node:first:lift:2', position: { x: 0, y: 28, z: -23 } },
  { id: 'node:first:crest', position: { x: 12, y: 32, z: -18 } },
  { id: 'node:first:drop:0', position: { x: 26, y: 17, z: -10 } },
  { id: 'node:first:drop:1', position: { x: 40, y: 5, z: -2 } },
  { id: 'node:first:turn:preloop:0', position: { x: 52, y: 5, z: 4 }, bankDegrees: 15 },
  { id: 'node:first:turn:preloop:1', position: { x: 66, y: 5, z: 14 }, bankDegrees: 30 },
  { id: 'node:first:turn:preloop:2', position: { x: 72, y: 5, z: 28 }, bankDegrees: 40 },
  { id: 'node:first:turn:preloop:3', position: { x: 70, y: 5, z: 42 }, bankDegrees: 40 },
  { id: 'node:first:turn:preloop:4', position: { x: 62, y: 4, z: 50 }, bankDegrees: 35 },
  { id: 'node:first:turn:preloop:5', position: { x: 50, y: 4, z: 56 }, bankDegrees: 55 },
  { id: 'node:first:turn:preloop:6', position: { x: 38, y: 4, z: 58 }, bankDegrees: 60 },
  { id: 'node:first:turn:preloop:7', position: { x: 30, y: 4, z: 52 }, bankDegrees: 55 },
  { id: 'node:first:turn:preloop:8', position: { x: 28, y: 4, z: 40 }, bankDegrees: 26 },
  { id: 'node:first:loop-entry', position: { x: 28, y: 4, z: 26 } },
  ...firstLoopProfile.map((point, index) => ({
    id: `node:first:loop:${index}`,
    position: {
      x: 28 - point.sideM,
      y: 4 + point.heightM * 0.75,
      z: 26 - point.forwardM,
    },
  })),
  { id: 'node:first:launch:0', position: { x: firstLoopExitX, y: 4, z: -6 } },
  { id: 'node:first:launch:1', position: { x: firstLoopExitX, y: 4, z: -18 } },
  {
    id: 'node:first:turn:postloop:0',
    position: { x: firstLoopExitX, y: 4, z: -32 },
    bankDegrees: -20,
  },
  { id: 'node:first:turn:postloop:1', position: { x: 20, y: 4, z: -44 }, bankDegrees: -32 },
  { id: 'node:first:turn:postloop:2', position: { x: 12, y: 4, z: -53 }, bankDegrees: -46 },
  { id: 'node:first:turn:postloop:3', position: { x: 0, y: 4, z: -56 }, bankDegrees: -60 },
  { id: 'node:first:turn:postloop:4', position: { x: -12, y: 4, z: -53 }, bankDegrees: -55 },
  { id: 'node:first:turn:postloop:5', position: { x: -18, y: 4, z: -45 }, bankDegrees: -42 },
  { id: 'node:first:turn:postloop:6', position: { x: -20, y: 4, z: -34 }, bankDegrees: -20 },
  { id: 'node:first:hill:0', position: { x: -18, y: 4, z: -24 }, bankDegrees: -14 },
  { id: 'node:first:hill:1', position: { x: -12, y: 12, z: -12 }, bankDegrees: -6 },
  { id: 'node:first:hill:2', position: { x: -8, y: 18, z: 0 } },
  { id: 'node:first:hill:3', position: { x: -10, y: 5, z: 14 } },
  { id: 'node:first:photo', position: { x: -10, y: 5, z: 24 } },
  { id: 'node:first:return:pre:0', position: { x: -12, y: 4, z: 32 }, bankDegrees: 20 },
  { id: 'node:first:return:pre:1', position: { x: -20, y: 4, z: 40 }, bankDegrees: 30 },
  { id: 'node:first:return:0', position: { x: -34, y: 4, z: 44 }, bankDegrees: 22 },
  { id: 'node:first:return:1', position: { x: -50, y: 4, z: 42 }, bankDegrees: 34 },
  { id: 'node:first:return:2', position: { x: -64, y: 4, z: 34 }, bankDegrees: 38 },
  { id: 'node:first:return:3', position: { x: -72, y: 4, z: 22 }, bankDegrees: 34 },
  { id: 'node:first:return:4', position: { x: -72, y: 4, z: 8 }, bankDegrees: 24 },
  { id: 'node:first:brake:0', position: { x: -68, y: 4, z: -6 }, bankDegrees: 12 },
  { id: 'node:first:brake:1', position: { x: -60, y: 4, z: -18 }, bankDegrees: 4 },
];

const firstCoasterBehaviors: TrackBehavior[] = firstCoasterNodes.map(() => ({ kind: 'normal' }));
firstCoasterBehaviors[0] = { kind: 'station', holdSeconds: 1, dispatchSpeedMps: 3.5 };
firstCoasterBehaviors[1] = { kind: 'station', holdSeconds: 0, dispatchSpeedMps: 3.5 };
firstCoasterBehaviors[2] = { kind: 'lift', targetSpeedMps: 6 };
firstCoasterBehaviors[3] = { kind: 'lift', targetSpeedMps: 6 };
firstCoasterBehaviors[4] = { kind: 'lift', targetSpeedMps: 6 };
firstCoasterBehaviors[5] = { kind: 'lift', targetSpeedMps: 6 };
const loopStartIndex = 18;
const loopEndIndex = loopStartIndex + firstLoopProfile.length;
firstCoasterBehaviors[loopEndIndex] = {
  kind: 'launch',
  targetSpeedMps: 16,
  accelerationMps2: 12.5,
};
firstCoasterBehaviors[loopEndIndex + 1] = {
  kind: 'launch',
  targetSpeedMps: 16,
  accelerationMps2: 12.5,
};
const firstPhotoIndex = firstCoasterNodes.findIndex((node) => node.id === 'node:first:photo');
firstCoasterBehaviors[firstPhotoIndex] = { kind: 'photo' };
for (const [index, node] of firstCoasterNodes.entries())
  if (node.id.startsWith('node:first:brake:'))
    firstCoasterBehaviors[index] = {
      kind: 'brake',
      targetSpeedMps: 8,
      decelerationMps2: 8,
    };

const firstCoasterPieces: Array<CoasterProject['editor']['pieceGroups'][number]['definitionId']> =
  firstCoasterNodes.map(() => 'straight');
firstCoasterPieces[0] = 'station';
firstCoasterPieces[1] = 'station';
firstCoasterPieces[2] = 'lift';
firstCoasterPieces[3] = 'lift';
firstCoasterPieces[4] = 'lift';
firstCoasterPieces[5] = 'slope_to_level';
firstCoasterPieces[6] = 'drop';
firstCoasterPieces[7] = 'drop';
firstCoasterPieces[8] = 'bank_in';
for (let index = 9; index < loopStartIndex; index += 1) firstCoasterPieces[index] = 'turn_right';
for (let index = loopStartIndex; index < loopEndIndex; index += 1)
  firstCoasterPieces[index] = 'loop_right';
firstCoasterPieces[loopEndIndex] = 'launch';
firstCoasterPieces[loopEndIndex + 1] = 'launch';
firstCoasterPieces[loopEndIndex + 2] = 'bank_in';
for (let index = loopEndIndex + 3; index <= loopEndIndex + 7; index += 1)
  firstCoasterPieces[index] = 'turn_right';
firstCoasterPieces[loopEndIndex + 8] = 'bank_out';
for (const [index, node] of firstCoasterNodes.entries())
  if (node.id.startsWith('node:first:hill:')) firstCoasterPieces[index] = 'hill';
firstCoasterPieces[firstPhotoIndex] = 'photo';
for (const [index, node] of firstCoasterNodes.entries()) {
  if (node.id.startsWith('node:first:brake:')) firstCoasterPieces[index] = 'brake';
  if (node.id.startsWith('node:first:return:')) firstCoasterPieces[index] = 'turn_right';
}

export const systemsCircuitCoasterFixture = makeProject(
  'fixture.systems-circuit',
  'First Coaster',
  firstCoasterNodes,
  firstCoasterBehaviors,
  true,
  firstCoasterPieces,
  { x: 160, z: 120 },
);

export const clearanceConflictCoasterFixture = makeProject(
  'fixture.clearance-conflict',
  'Clearance Conflict',
  [
    { id: 'node:clearance:0', position: { x: -24, y: 4, z: 0 } },
    { id: 'node:clearance:1', position: { x: -12, y: 4, z: 14 } },
    { id: 'node:clearance:2', position: { x: 0, y: 4, z: 0 } },
    { id: 'node:clearance:3', position: { x: 12, y: 4, z: -14 } },
    { id: 'node:clearance:4', position: { x: 24, y: 4, z: 0 } },
    { id: 'node:clearance:5', position: { x: 12, y: 4, z: 14 } },
    { id: 'node:clearance:6', position: { x: 0, y: 4, z: 0 } },
    { id: 'node:clearance:7', position: { x: -12, y: 4, z: -14 } },
  ],
  Array.from({ length: 8 }, () => ({ kind: 'normal' }) as const),
);

function createPhase9GoldenVisualFixture(): CoasterProject {
  const project = structuredClone(systemsCircuitCoasterFixture);
  project.projectId = 'fixture.phase9-golden';
  project.revisionId = 'fixture.phase9-golden:fixture-v1';
  project.metadata.title = 'Phase 9 Golden Visual Fixture';
  project.metadata.description =
    'Internal lighting, material, train, track and media review fixture';
  project.metadata.tags = ['fixture', 'phase9_golden', 'internal_review'];
  project.appearance.trackColor = '#f2b632';
  project.appearance.supportColor = '#263746';
  project.appearance.trainColor = '#f5c842';
  project.appearance.stationColor = '#d8cbb5';
  project.scenery = [
    {
      id: 'scenery:phase9:tree',
      assetId: 'tree',
      position: { x: -30, y: 0, z: 18 },
      rotationDegrees: { x: 0, y: 20, z: 0 },
      scale: { x: 1.2, y: 1.2, z: 1.2 },
      colorVariant: null,
    },
    {
      id: 'scenery:phase9:hedge',
      assetId: 'hedge',
      position: { x: -18, y: 0, z: 20 },
      rotationDegrees: { x: 0, y: -12, z: 0 },
      scale: { x: 1.1, y: 1, z: 1 },
      colorVariant: null,
    },
    {
      id: 'scenery:phase9:lamp',
      assetId: 'lamp',
      position: { x: -8, y: 0, z: 18 },
      rotationDegrees: { x: 0, y: 0, z: 0 },
      scale: { x: 1, y: 1, z: 1 },
      colorVariant: null,
    },
    {
      id: 'scenery:phase9:stone',
      assetId: 'stone',
      position: { x: 2, y: 0, z: 18 },
      rotationDegrees: { x: 0, y: 18, z: 0 },
      scale: { x: 1.2, y: 0.8, z: 1 },
      colorVariant: null,
    },
  ];

  const ordinaryGroups = project.editor.pieceGroups.filter((piece) =>
    ['turn_right', 'hill', 'straight'].includes(piece.definitionId),
  );
  const roll = ordinaryGroups[Math.floor(ordinaryGroups.length * 0.35)];
  const corkscrew = ordinaryGroups[Math.floor(ordinaryGroups.length * 0.7)];
  if (roll) roll.definitionId = 'roll_right';
  if (corkscrew) corkscrew.definitionId = 'corkscrew_left';
  return project;
}

export const phase9GoldenVisualFixture = createPhase9GoldenVisualFixture();

function createLoopworksShowcaseFixture(): CoasterProject {
  const project = structuredClone(phase9GoldenVisualFixture);
  project.projectId = 'first-party.the-loopworks';
  project.revisionId = 'first-party.the-loopworks:launch-v1';
  project.metadata.title = 'The Loopworks';
  project.metadata.description =
    'A bright Park Model steel coaster packed with workshop-sized thrills.';
  project.metadata.tags = ['first_party', 'showcase', 'park_model', 'inversions'];
  project.metadata.creator = { kind: 'local', id: null, displayName: 'Buncha Coasters' };
  project.permissions = { visibility: 'private', allowReadOnlyShare: true, allowRemix: true };
  project.appearance.trackColor = '#1878d1';
  project.appearance.supportColor = '#243847';
  project.appearance.trainColor = '#e5553f';
  project.scenery = [
    ['plaza', -34, -16, 0],
    ['canopy', -30, -14, 0],
    ['kiosk', -38, -12, 90],
    ['bench', -26, -19, 0],
    ['bin', -24, -18, 0],
    ['lamp', -40, -19, 0],
    ['lamp', -20, -19, 0],
    ['directional_sign', -35, -23, 15],
    ['path_straight', -16, -17, 0],
    ['path_straight', -8, -17, 0],
    ['path_corner', 0, -17, 0],
    ['broadleaf_tree', -43, 20, 0],
    ['broadleaf_tree', 42, 22, 0],
    ['conifer', 35, -28, 0],
    ['conifer', -42, -28, 0],
    ['shrub_cluster', -20, 18, 0],
    ['shrub_cluster', 22, 18, 0],
    ['flower_bed', -28, -24, 0],
    ['flower_bed', -18, -24, 0],
    ['rock_cluster', 38, 8, 0],
  ].map(([assetId, x, z, rotation], index) => ({
    id: `scenery:loopworks:${index}`,
    assetId: String(assetId),
    position: { x: Number(x), y: 0, z: Number(z) },
    rotationDegrees: { x: 0, y: Number(rotation), z: 0 },
    scale: { x: 1, y: 1, z: 1 },
    colorVariant: null,
  }));
  return project;
}

export const loopworksShowcaseFixture = createLoopworksShowcaseFixture();

export const stallHillCoasterFixture = makeProject(
  'fixture.stall-hill',
  'Stall Test',
  [
    { id: 'node:stall:0', position: { x: -25, y: 4, z: -12 } },
    { id: 'node:stall:1', position: { x: -5, y: 4, z: -12 } },
    { id: 'node:stall:2', position: { x: 8, y: 16, z: -7 } },
    { id: 'node:stall:3', position: { x: 14, y: 65, z: 0 } },
    { id: 'node:stall:4', position: { x: 2, y: 10, z: 18 }, bankDegrees: 28 },
    { id: 'node:stall:5', position: { x: -22, y: 4, z: 12 }, bankDegrees: 18 },
  ],
  [
    { kind: 'station', holdSeconds: 0.1, dispatchSpeedMps: 4 },
    { kind: 'lift', targetSpeedMps: 5 },
    { kind: 'normal' },
    { kind: 'normal' },
    { kind: 'brake', targetSpeedMps: 6, decelerationMps2: 4 },
    { kind: 'normal' },
  ],
);

const loopFixtureNodes: FixtureNode[] = [
  { id: 'node:loop:approach:0', position: { x: -45, y: 4, z: 0 } },
  { id: 'node:loop:approach:1', position: { x: -24, y: 4, z: 0 } },
  { id: 'node:loop:entry', position: { x: -8, y: 4, z: 0 } },
  ...generateVerticalLoopProfile().map((point, index) => ({
    id: `node:loop:profile:${index}`,
    position: { x: -8 + point.forwardM, y: 4 + point.heightM, z: point.sideM },
  })),
  { id: 'node:loop:exit:0', position: { x: 26, y: 4, z: 4 } },
  { id: 'node:loop:exit:1', position: { x: 42, y: 5, z: 15 }, bankDegrees: 16 },
  { id: 'node:loop:return:0', position: { x: 24, y: 6, z: 34 }, bankDegrees: 24 },
  { id: 'node:loop:return:1', position: { x: -12, y: 5, z: 36 }, bankDegrees: 20 },
  { id: 'node:loop:return:2', position: { x: -40, y: 4, z: 24 }, bankDegrees: 12 },
  { id: 'node:loop:return:3', position: { x: -49, y: 4, z: 9 } },
];

const loopFixtureBehaviors: TrackBehavior[] = loopFixtureNodes.map(() => ({ kind: 'normal' }));
loopFixtureBehaviors[0] = { kind: 'station', holdSeconds: 2, dispatchSpeedMps: 2 };
loopFixtureBehaviors[1] = { kind: 'launch', targetSpeedMps: 34, accelerationMps2: 18 };
loopFixtureBehaviors[2] = { kind: 'launch', targetSpeedMps: 34, accelerationMps2: 18 };
for (let index = loopFixtureBehaviors.length - 3; index < loopFixtureBehaviors.length; index += 1)
  loopFixtureBehaviors[index] = {
    kind: 'brake',
    targetSpeedMps: 2,
    decelerationMps2: 10,
  };

export const loopCoasterFixture = makeProject(
  'fixture.loop',
  'Refined Vertical Loop',
  loopFixtureNodes,
  loopFixtureBehaviors,
);

export const invalidCoasterFixture: unknown = {
  ...ovalCoasterFixture,
  track: {
    ...ovalCoasterFixture.track,
    nodes: [
      ovalCoasterFixture.track.nodes[0],
      {
        ...ovalCoasterFixture.track.nodes[1],
        id: ovalCoasterFixture.track.nodes[0]!.id,
        position: { x: Number.NaN, y: 4, z: 0 },
      },
    ],
    sections: [{ ...ovalCoasterFixture.track.sections[0], toNodeId: 'node:missing' }],
  },
};

export const validCoasterFixtures = [
  blankCoasterFixture,
  ovalCoasterFixture,
  liftDropCoasterFixture,
  systemsCircuitCoasterFixture,
  phase9GoldenVisualFixture,
  loopworksShowcaseFixture,
  stallHillCoasterFixture,
  loopCoasterFixture,
] as const;
